import type { KeyedMutator } from 'swr';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  GripVertical,
  Home,
  UsersRound,
  Calendar,
  PlusSquare,
  Building2,
  User,
  Target,
  Handshake,
} from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { NavigationItemId, NavigationConfig, Community } from '@/types';
import { cn } from '@/lib/utils';

interface NavigationSettingsProps {
  communityId: string;
  mutateCommunity: KeyedMutator<{ community: Community }>;
  navigationConfig: NavigationConfig | null;
  setNavigationConfig: (config: NavigationConfig) => void;
}

const navItemMetadata: Record<
  NavigationItemId,
  { label: string; icon: React.ElementType; description: string }
> = {
  home: { label: 'Dashboard', icon: Home, description: 'Main community dashboard' },
  members: { label: 'Members', icon: UsersRound, description: 'View all community members' },
  events: { label: 'Events', icon: Calendar, description: 'Community events and meetups' },
  projects: { label: 'Projects', icon: Target, description: 'Community projects and initiatives' },
  create: { label: 'Create', icon: PlusSquare, description: 'Create posts and content' },
  business: { label: 'Directory', icon: Building2, description: 'Business directory' },
  'gives-asks': { label: 'Gives & Asks', icon: Handshake, description: 'Community gives and asks' },
  profile: { label: 'Profile', icon: User, description: 'User profile page' },
};

export function NavigationSettings({
  communityId,
  mutateCommunity,
  navigationConfig,
  setNavigationConfig,
}: NavigationSettingsProps) {
  const [items, setItems] = useState<NavigationItemId[]>(
    (navigationConfig?.items || ['home', 'members', 'events', 'create', 'business', 'profile']).filter(
      (id) => id !== 'gives-asks'
    )
  );
  const [draggedItem, setDraggedItem] = useState<NavigationItemId | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (navigationConfig?.items) {
      setItems(navigationConfig.items);
    }
  }, [navigationConfig]);

  const handleDragStart = (item: NavigationItemId) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e: React.DragEvent, targetItem: NavigationItemId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetItem) return;

    const newItems = [...items];
    const draggedIndex = newItems.indexOf(draggedItem);
    const targetIndex = newItems.indexOf(targetItem);

    newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    setItems(newItems);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const toggleItem = (item: NavigationItemId) => {
    if (item === 'profile') {
      toast.error(`${navItemMetadata[item].label} is mandatory and cannot be disabled`);
      return;
    }

    const newItems = items.includes(item) ? items.filter((i) => i !== item) : [...items, item];

    setItems(newItems);
  };

  const saveNavigationConfig = async () => {
    if (!items.includes('profile')) {
      toast.error('My Profile must remain in the navigation');
      return;
    }
    if (!items.some((i) => i !== 'profile')) {
      toast.error('Enable at least one tab besides My Profile');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        navigationConfig: { items },
      });
      setNavigationConfig({ items });
      await mutateCommunity();
      toast.success('Navigation settings updated');
    } catch (error) {
      console.error('Failed to update navigation config:', error);
      toast.error('Failed to update navigation settings');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    JSON.stringify(items) !==
    JSON.stringify(
      (navigationConfig?.items || ['home', 'members', 'events', 'create', 'business', 'profile']).filter(
        (id) => id !== 'gives-asks'
      )
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Navigation Configuration</CardTitle>
        <CardDescription>
          Customize which navigation items appear and their order. Drag to reorder, click to toggle.
          My Profile stays required; enable at least one other tab (you can hide the dashboard if
          another entry point is preferred). The `/dashboard` URL still works for bookmarks and
          onboarding.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2 sm:space-y-3">
          {/* Render enabled items in their configured order */}
          {items.map((id, index) => {
            const metadata = navItemMetadata[id];
            if (!metadata) return null;

            const isMandatory = id === 'profile';
            const Icon = metadata.icon;

            return (
              <div
                key={id}
                draggable={true}
                onDragStart={() => handleDragStart(id)}
                onDragOver={(e) => handleDragOver(e, id)}
                onDragEnd={handleDragEnd}
                className={cn(
                  'flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all',
                  'bg-background border-border hover:border-primary/50 cursor-move',
                  draggedItem === id && 'opacity-50'
                )}
              >
                <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground shrink-0">
                  <GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-xs sm:text-sm font-mono w-4 sm:w-6">{index + 1}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="p-1.5 sm:p-2 rounded-lg shrink-0 bg-primary/10 text-primary">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm sm:text-base">{metadata.label}</p>
                      {isMandatory && (
                        <span className="text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium whitespace-nowrap">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                      {metadata.description}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleItem(id)}
                  disabled={isMandatory}
                  className="shrink-0 text-xs sm:text-sm"
                >
                  Hide
                </Button>
              </div>
            );
          })}

          {/* Render disabled items */}
          {Object.entries(navItemMetadata)
            .filter(([itemId]) => !items.includes(itemId as NavigationItemId))
            .map(([itemId, metadata]) => {
              const id = itemId as NavigationItemId;
              const Icon = metadata.icon;

              return (
                <div
                  key={id}
                  className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all bg-muted/30 border-dashed border-muted-foreground/20 opacity-60"
                >
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="p-1.5 sm:p-2 rounded-lg shrink-0 bg-muted text-muted-foreground">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm sm:text-base">{metadata.label}</p>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                        {metadata.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => toggleItem(id)}
                    className="shrink-0 text-xs sm:text-sm"
                  >
                    Show
                  </Button>
                </div>
              );
            })}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            <p>
              <strong>{items.length}</strong> items enabled • All items will show on mobile via a
              scrollable bar
            </p>
          </div>
          <Button
            onClick={saveNavigationConfig}
            disabled={!hasChanges || saving}
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
